import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Phaser from 'phaser';
import { GameOverScene } from '../scenes/GameOverScene';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import type { GameState } from '../types/game';

// ── Types ─────────────────────────────────────────────────────

type Scenario = 'round-win' | 'round-loss' | 'round-blocked' | 'session-win' | 'session-loss';

const SCENARIOS: Array<{ id: Scenario; label: string }> = [
  { id: 'round-win',     label: 'Round Win'     },
  { id: 'round-loss',    label: 'Round Loss'    },
  { id: 'round-blocked', label: 'Blocked Game'  },
  { id: 'session-win',   label: 'Session Win'   },
  { id: 'session-loss',  label: 'Session Loss'  },
];

// ── Mock data ─────────────────────────────────────────────────

const MOCK_PLAYERS = [
  { id: 1, email: 'you@example.com',       team: 0 as const, position: 0 as const },
  { id: 2, email: 'partner@example.com',   team: 0 as const, position: 2 as const },
  { id: 3, email: 'opponent1@example.com', team: 1 as const, position: 1 as const },
  { id: 4, email: 'opponent2@example.com', team: 1 as const, position: 3 as const },
];

const EMPTY_BOARD = {
  board_config: { min_x: 0, max_x: 0, min_y: 0, max_y: 0, tile_width: 1, tile_height: 2 },
  tiles: [],
  board_ends: [],
  chain_length: 0,
};

function buildSceneData(scenario: Scenario, myTeam: 0 | 1) {
  const myTeamWins  = scenario.endsWith('-win');
  const winnerTeam: 0 | 1 = myTeamWins ? myTeam : ((1 - myTeam) as 0 | 1);
  const sessionOver = scenario.startsWith('session-');
  const isBlocked   = scenario === 'round-blocked';

  const gameState: GameState = {
    id:                       999,
    status:                   'finished',
    current_turn:             0,
    current_turn_user_id:     1,
    current_turn_team:        0,
    board_left_end:           3,
    board_right_end:          5,
    played_tiles:             [],
    winner_team:              winnerTeam,
    game_end_type:            isBlocked ? 'blocked' : 'win',
    session_id:               999,
    game_number:              2,
    starting_player_position: 0,
    starting_player_user_id:  1,
    board_layout:             EMPTY_BOARD,
    board_chain_length:       12,
    players:                  MOCK_PLAYERS,
    hand_counts:              { '0': 3, '1': 5, '2': 2, '3': 4 },
  };

  // Session-over: one team has crossed 100. Round-over: plausible mid-session scores.
  const teamScores = sessionOver
    ? { '0': winnerTeam === 0 ? 105 : 68, '1': winnerTeam === 1 ? 105 : 68 }
    : { '0': 45, '1': 72 };

  return {
    gameState,
    sessionId:         999,
    teamScores,
    myPosition:        0,
    myTeam,
    sessionOver,
    sessionWinnerTeam: sessionOver ? winnerTeam : null,
    targetScore:       100,
    useDesktopRender:  IS_MOBILE_EXPERIENCE,
  };
}

// ── Phaser boot scenes ────────────────────────────────────────

// Reads injected mock data and starts GameOverScene directly.
class DevBootScene extends Phaser.Scene {
  constructor() { super({ key: 'DevBootScene' }); }
  create(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (window as any).__DEV_GAMEOVER_DATA__;
    this.scene.start('GameOverScene', data ?? {});
  }
}

// Stub: if auto-advance or "Next Round" succeeds and starts GameScene,
// exit back to the dev screen instead of crashing ("scene not found").
class DevGameStubScene extends Phaser.Scene {
  constructor() { super({ key: 'GameScene' }); }
  create(): void { window.dispatchEvent(new CustomEvent('phaser-dev-exit')); }
}

// Stub: intercepts GameOverScene's scene.start('MenuScene').
class DevMenuStubScene extends Phaser.Scene {
  constructor() { super({ key: 'MenuScene' }); }
  create(): void { window.dispatchEvent(new CustomEvent('phaser-dev-exit')); }
}

// ── Control bar styles ────────────────────────────────────────

const BAR_H = 52;

const barBase: React.CSSProperties = {
  height: BAR_H, flexShrink: 0,
  background: '#0e0e0e',
  borderBottom: '1px solid rgba(255,255,255,0.07)',
  display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px',
  overflowX: 'auto', overflowY: 'hidden',
};

function chip(active: boolean, color?: string): React.CSSProperties {
  return {
    padding: '5px 11px', borderRadius: 6, border: 'none', cursor: 'pointer',
    background:  active ? (color ?? '#D4920A') : 'rgba(255,255,255,0.05)',
    color:       active ? '#000' : '#999',
    fontSize:    11, fontWeight: active ? 700 : 400,
    whiteSpace:  'nowrap', flexShrink: 0,
    transition:  'background 0.12s',
  };
}

const sep: React.CSSProperties = {
  width: 1, height: 22, background: 'rgba(255,255,255,0.08)',
  margin: '0 2px', flexShrink: 0,
};

// ── Screen ────────────────────────────────────────────────────

export function DevGameOverScreen() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState<Scenario>('round-win');
  const [myTeam,   setMyTeam]   = useState<0 | 1>(0);
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__DEV_GAMEOVER_DATA__ = buildSceneData(scenario, myTeam);

    const game = new Phaser.Game({
      type:            Phaser.AUTO,
      parent:          mountRef.current,
      backgroundColor: '#000000',
      scene:           [DevBootScene, GameOverScene, DevGameStubScene, DevMenuStubScene],
      scale:           { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.CENTER_BOTH },
    });

    const handleExit = () => navigate(-1);
    window.addEventListener('phaser-dev-exit', handleExit);

    return () => {
      window.removeEventListener('phaser-dev-exit', handleExit);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).__DEV_GAMEOVER_DATA__;
      try { game.destroy(true); } catch { /* ignore teardown errors */ }
    };
  }, [scenario, myTeam, navigate]);

  const teamColor = myTeam === 0 ? '#E05252' : '#4A90D9';

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', flexDirection: 'column' }}>

      {/* ── Control bar ──────────────────────────────────────── */}
      <div style={barBase}>

        <span style={{ color: '#555', fontSize: 10, fontFamily: 'monospace', letterSpacing: 1, marginRight: 4, flexShrink: 0 }}>
          DEV
        </span>

        {SCENARIOS.map(s => (
          <button key={s.id} style={chip(scenario === s.id)} onClick={() => setScenario(s.id)}>
            {s.label}
          </button>
        ))}

        <div style={sep} />

        <button
          style={{ ...chip(false), color: teamColor, border: `1px solid ${teamColor}33` }}
          onClick={() => setMyTeam(t => t === 0 ? 1 : 0)}
        >
          {myTeam === 0 ? 'Team A' : 'Team B'}
        </button>

        <div style={{ flex: 1 }} />

        <button
          style={{ ...chip(false), color: '#777' }}
          onClick={() => navigate(-1)}
        >
          ✕ Close
        </button>

      </div>

      {/* ── Phaser canvas ─────────────────────────────────────── */}
      <div ref={mountRef} style={{ flex: 1, overflow: 'hidden' }} />

    </div>
  );
}
